import type { LayoutAlgorithm, LayoutDirection } from "../contracts"
import { createWorkerLayoutEngine, NODE_HEIGHT, NODE_WIDTH } from "./engine"

export type { LayoutAlgorithm, LayoutDirection }
export { NODE_HEIGHT, NODE_WIDTH }

export const LAYOUT_ALGORITHMS: readonly {
  id: LayoutAlgorithm
  label: string
  desc: string
}[] = [
  {
    id: "layered",
    label: "Layered",
    desc: "Hierarchical Sugiyama — slow on large graphs",
  },
  { id: "mrtree", label: "Tree", desc: "Fast tree layout" },
  {
    id: "stress",
    label: "Stress",
    desc: "Stress minimization — good for clusters",
  },
  { id: "force", label: "Force", desc: "Force-directed — fast, organic" },
  { id: "radial", label: "Radial", desc: "Radial tree layout" },
]

export interface LayoutNode {
  id: string
  width?: number
  height?: number
  kind?: string
}

export interface LayoutEdge {
  id: string
  source: string
  target: string
  edgeKind?: string
}

export interface LayoutResultNode {
  id: string
  x: number
  y: number
  width: number
  height: number
}

export interface LayoutRequest {
  nodes: readonly LayoutNode[]
  edges: readonly LayoutEdge[]
  direction?: LayoutDirection
  algorithm?: LayoutAlgorithm
  signal?: AbortSignal
}

export interface LayoutResult {
  nodes: readonly LayoutResultNode[]
}

export interface LayoutEngine {
  layout(request: LayoutRequest): Promise<LayoutResult>
  dispose(): void
}

export function createLayoutEngine(): LayoutEngine {
  return createWorkerLayoutEngine()
}

let convenienceEngine: LayoutEngine | null = null

/**
 * Convenience layout function for application code that wants a shared,
 * process-lifetime engine. Consumers that need lifecycle ownership should use
 * createLayoutEngine() and dispose their instance.
 */
export async function layoutGraph(
  nodes: readonly LayoutNode[],
  edges: readonly LayoutEdge[],
  direction: LayoutDirection = "DOWN",
  algorithm: LayoutAlgorithm = "layered",
  signal?: AbortSignal,
): Promise<LayoutResultNode[]> {
  convenienceEngine ??= createLayoutEngine()
  const result = await convenienceEngine.layout({
    nodes,
    edges,
    direction,
    algorithm,
    signal,
  })
  return [...result.nodes]
}
